import { chain } from 'lodash'

// NOTE: セット(60分) x 3 とかやらなきゃなので（CourseHistories.vueから移設）
export const groupCourseHistories = (vendingHistories, hhmm) => {
  return chain(vendingHistories)
    .filter(vendingHistory => vendingHistory.attributes.vendibleType === 'Course')
    // NOTE: 表示は新しい順（直近の注文が一番上）。ラベル(1セット目/Nセット目/延長N)と
    // 取消対象の判定は表示順に依存しない
    .orderBy(['attributes.persistenceReport.data.attributes.startAt'], ['desc'])
    .groupBy(courseHistory => {
      const { vendibleId, vendibleName, orderGroupId } = courseHistory.attributes
      const { startAt, releaseAt } = courseHistory.attributes.persistenceReport.data?.attributes || {}
      if (!(startAt && releaseAt)) return null

      // NOTE: orderGroupId(同一リクエスト=1オーダー)があればキーに含め、別オーダーの
      // 同一コース・同時刻が誤って結合されないようにする。時刻キーも残すのは、
      // 個別入店モード(1人ずつstartAtが異なる)の分割表示を維持するため。
      // 旧データ(orderGroupId無し)は従来の時刻一致グルーピングにフォールバックする。
      const timeKey = `${vendibleId}${vendibleName}${hhmm(startAt)}${hhmm(releaseAt)}`

      return orderGroupId ? `og:${orderGroupId}:${timeKey}` : timeKey
    })
    .mapValues((courseHistoriesGrouped, key) => {
      const { vendibleId, vendibleName } = courseHistoriesGrouped[0]?.attributes
      const { startAt, releaseAt } = courseHistoriesGrouped[0]?.attributes?.persistenceReport?.data?.attributes || {}

      return {
        key,
        vendibleId,
        vendibleName,
        orderGroupId: courseHistoriesGrouped[0]?.attributes?.orderGroupId || null,
        startAt,
        releaseAt,
        // NOTE: 注文された時刻。同一startAt（客数追加で同じ時間帯のセットが並ぶ）の
        // 表示順・セット採番のタイブレークに使う
        createdAt: courseHistoriesGrouped
          .map(vendingHistory => vendingHistory.attributes?.createdAt)
          .filter(Boolean)
          .sort()[0] || null,
        size: courseHistoriesGrouped.length,
        actualData: courseHistoriesGrouped,
      }
    })
    .values()
    // NOTE: グループの表示順。開始時刻が同じ場合は後から注文した方を上にする
    .orderBy(['startAt', 'createdAt'], ['desc', 'desc'])
    .value()
}

export const totalStayMinutes = (startAt, releaseAt) => {
  if (!(startAt && releaseAt)) return null

  // NOTE: 入店時刻には秒が乗る一方で編集後の終了時刻は分精度のため、
  // 表示時刻（HH:mm）と一致するよう双方を分精度に切り捨ててから差分を取る
  const minuteFloor = at => Math.floor(Date.parse(at) / 60000)

  return minuteFloor(releaseAt) - minuteFloor(startAt)
}

// NOTE: グループを「セット(1セット目/Nセット目) / 延長1..N」のセグメントにする。
// 延長かどうかは「開始時刻が既存グループのいずれかの終了時刻と一致するか」で判定する
// （バックエンド build_new_persistence_reports が延長の start_at を現状の終了時刻に積む仕様の鏡写し）。
// 一致しないグループは客数追加などの「追加セット」（Nセット目）として扱う。
export const buildSessionSegments = groups => {
  const timedGroups = groups.filter(group => group.startAt && group.releaseAt)

  // NOTE: 手動の時間編集は全レポートを一律シフトするため連結関係は保たれる。分精度で比較する
  const minuteKey = at => Math.floor(Date.parse(at) / 60000)

  // NOTE: 採番は古い順。開始時刻が同じ場合は先に注文した方を若い番号(1セット目)にする
  const ascending = [...timedGroups].sort((a, b) => (
    (Date.parse(a.startAt) - Date.parse(b.startAt))
    || String(a.createdAt || '').localeCompare(String(b.createdAt || ''))
  ))
  const labelByKey = new Map()
  const releaseKeys = new Set()
  let setCount = 0
  let extensionCount = 0

  ascending.forEach(group => {
    if (releaseKeys.has(minuteKey(group.startAt))) {
      extensionCount += 1
      labelByKey.set(group.key, { kind: 'extension', label: `延長 ${extensionCount}` })
    } else {
      setCount += 1
      labelByKey.set(group.key, { kind: 'set', label: setCount === 1 ? '1セット目' : `${setCount}セット目` })
    }

    releaseKeys.add(minuteKey(group.releaseAt))
  })

  return timedGroups.map(group => {
    const { kind, label } = labelByKey.get(group.key)
    const unitPrice = Number(group.actualData[0]?.attributes?.sellingPrice) || 0

    return {
      ...group,
      kind,
      label,
      autoCreated: group.actualData.some(vendingHistory => vendingHistory.attributes?.autoCreated === true),
      unitPrice,
      subtotal: group.actualData.reduce((acc, vendingHistory) => acc + (Number(vendingHistory.attributes?.sellingPrice) || 0), 0),
      durationMinutes: totalStayMinutes(group.startAt, group.releaseAt),
    }
  })
}

export const isExtending = segments => {
  return segments.some(segment => segment.kind === 'extension')
}

// NOTE: 取り消せるのは時系列で最後（最新）のセグメントが延長のときのみ。
// 表示順に依存しないよう startAt で最新を求める
export const cancellableSegment = segments => {
  if (segments.length === 0) return null

  const newest = [...segments].sort((a, b) => Date.parse(b.startAt) - Date.parse(a.startAt))[0]
  if (!newest || newest.kind !== 'extension') return null

  return newest
}

// NOTE: 明細の表示名。指名など受領者(キャスト)がいる場合は「商品名（名前）」形式にして
// 同名明細(本指名×2等)を区別できるようにする
export const companionLabel = vendingHistory => {
  const vendibleName = vendingHistory.attributes?.vendibleName || ''
  const recipientNames = (vendingHistory.attributes?.vendibleRecipients?.data || [])
    .map(vendibleRecipient => vendibleRecipient.attributes?.recipient?.data?.attributes?.name)
    .filter(Boolean)

  if (recipientNames.length === 0) return vendibleName

  return `${vendibleName}（${recipientNames.join('、')}）`
}

// NOTE: 同一オーダー(orderGroupId)で同時に注文されたコース以外の明細(指名・アイテム)。
// 延長カードのまとめ表示と、まとめて取消の対象に使う。旧データ(orderGroupId無し)は常に空。
export const collectCompanions = (vendingHistories, orderGroupId) => {
  if (!orderGroupId) return []

  return vendingHistories.filter(vendingHistory => (
    vendingHistory.attributes?.orderGroupId === orderGroupId
    && vendingHistory.attributes?.vendibleType !== 'Course'
  ))
}

export const buildDestroyPayload = (segment, companions = []) => {
  return [...segment.actualData, ...companions].map(vendingHistory => ({
    id: vendingHistory.id,
    _destroy: true,
  }))
}

export default () => {
  return {
    groupCourseHistories,
    buildSessionSegments,
    isExtending,
    totalStayMinutes,
    cancellableSegment,
    companionLabel,
    collectCompanions,
    buildDestroyPayload,
  }
}
